import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '@src/store/base-query';

export const referralsApi = createApi({
  reducerPath: 'referralsQueries',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ReferralCode', 'ReferralStats', 'ReferralList', 'PayoutHistory', 'ConnectStatus', 'AdminReferrals'],
  endpoints: (builder) => ({
    getReferralCode: builder.query({
      query: () => 'referrals/code',
      providesTags: ['ReferralCode'],
    }),

    generateReferralCode: builder.mutation({
      query: () => ({
        url: 'referrals/code',
        method: 'POST',
      }),
      invalidatesTags: ['ReferralCode', 'ReferralStats'],
    }),

    setCustomSlug: builder.mutation({
      query: (slug) => ({
        url: 'referrals/code/slug',
        method: 'PUT',
        body: { slug },
      }),
      invalidatesTags: ['ReferralCode'],
    }),

    getReferralStats: builder.query({
      query: () => 'referrals/stats',
      providesTags: ['ReferralStats'],
    }),

    getReferralsList: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => `referrals/list?page=${page}&limit=${limit}`,
      providesTags: ['ReferralList'],
    }),

    getPayoutHistory: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => `referrals/payouts?page=${page}&limit=${limit}`,
      providesTags: ['PayoutHistory'],
    }),

    getPendingCommissions: builder.query({
      query: () => 'referrals/commissions',
    }),

    getTaxSummary: builder.query({
      query: () => 'referrals/tax-summary',
      providesTags: ['PayoutHistory'],
    }),

    validateReferralCode: builder.mutation({
      query: (code) => ({
        url: `referrals/validate/${code}`,
        method: 'POST',
      }),
    }),

    setupStripeConnect: builder.mutation({
      query: () => ({
        url: 'referrals/connect/setup',
        method: 'POST',
      }),
      invalidatesTags: ['ConnectStatus'],
    }),

    getConnectStatus: builder.query({
      query: () => 'referrals/connect/status',
      providesTags: ['ConnectStatus'],
    }),

    getAdminReferralAnalytics: builder.query({
      query: () => 'referrals/admin/analytics',
      providesTags: ['AdminReferrals'],
    }),

    getAdminReferralsList: builder.query({
      query: ({ page = 1, limit = 20, status } = {}) => {
        let url = `referrals/admin/list?page=${page}&limit=${limit}`;

        if (status) url += `&status=${status}`;

        return url;
      },
      providesTags: ['AdminReferrals'],
    }),

    getAdminFlaggedReferrals: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => `referrals/admin/flagged?page=${page}&limit=${limit}`,
      providesTags: ['AdminReferrals'],
    }),

    adminUpdateReferralStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `referrals/admin/${id}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['AdminReferrals'],
    }),

    adminTriggerPayout: builder.mutation({
      query: (userId) => ({
        url: `referrals/admin/${userId}/payout`,
        method: 'POST',
      }),
      invalidatesTags: ['AdminReferrals', 'PayoutHistory', 'ReferralStats'],
    }),

    requestPayout: builder.mutation({
      query: (amountCents) => ({
        url: 'referrals/payout/request',
        method: 'POST',
        body: { amountCents },
      }),
      invalidatesTags: ['ReferralStats', 'PayoutHistory'],
    }),

    adminAddBalance: builder.mutation({
      query: ({ userId, amountCents }) => ({
        url: `referrals/admin/${userId}/add-balance`,
        method: 'POST',
        body: { amountCents },
      }),
      invalidatesTags: ['ReferralStats'],
    }),
  }),
});

export const {
  useGetReferralCodeQuery,
  useGenerateReferralCodeMutation,
  useSetCustomSlugMutation,
  useGetReferralStatsQuery,
  useGetReferralsListQuery,
  useGetPayoutHistoryQuery,
  useGetPendingCommissionsQuery,
  useValidateReferralCodeMutation,
  useSetupStripeConnectMutation,
  useGetConnectStatusQuery,
  useGetAdminReferralAnalyticsQuery,
  useGetTaxSummaryQuery,
  useGetAdminReferralsListQuery,
  useGetAdminFlaggedReferralsQuery,
  useAdminUpdateReferralStatusMutation,
  useAdminTriggerPayoutMutation,
  useAdminAddBalanceMutation,
  useRequestPayoutMutation,
} = referralsApi;
